import type { ApiError } from './errors';
import { hasCode, TransportError } from './errors';

/**
 * Whether, and after how long, a failed READ is asked again. The policy `QueryProvider` installs.
 *
 * A module of its own so the unit tier can drive it with the errors the client really throws,
 * rather than through a rendered tree and a clock. The reasoning is on the provider.
 */

/** The longest `Retry-After` a query waits out by itself. The proxy's own answer is five. */
export const SHORT_RATE_LIMIT_SECONDS = 10;

/** The wait a rate-limited read is retried after, or `null` when it is not one to wait out. */
function shortRateLimitOf(error: unknown): number | null {
  if (!hasCode(error, 'SYSTEM_RATE_LIMITED')) {
    return null;
  }
  const seconds = (error as ApiError).retryAfterSeconds;
  return seconds !== null && seconds <= SHORT_RATE_LIMIT_SECONDS ? seconds : null;
}

export function retryQuery(failureCount: number, error: unknown): boolean {
  if (shortRateLimitOf(error) !== null) {
    return failureCount < 1;
  }
  // A refusal the server described is never retried; only a request that never reached it is.
  return error instanceof TransportError && failureCount < 2;
}

export function retryQueryDelay(failureCount: number, error: unknown): number {
  const seconds = shortRateLimitOf(error);
  // TanStack's own backoff for everything else, written out so this function is the whole policy.
  return seconds === null ? Math.min(1000 * 2 ** failureCount, 30_000) : seconds * 1000;
}
